import { Injectable } from '@angular/core';
import { TokenService } from '../token/token.service';
import { environment as env } from 'src/environments/environment';
import { MenuItem } from 'src/app/models/shared/layout/layout.interface';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, filter, Observable } from 'rxjs';
import { WebTypeConstant } from 'src/app/shared/constants/common-constant';
import { Base64Service } from '../url/base64.service';

@Injectable({
    providedIn: 'root'
})
export class MenuService {
    private accessListSubject = new BehaviorSubject<any[]>([]);
    accessList$: Observable<any[]> = this.accessListSubject.asObservable();

    constructor(
        private tokenService: TokenService,
        private router: Router,
        private base64Service: Base64Service
    ) {
        this.router.events
            .pipe(filter(event => event instanceof NavigationEnd))
            .subscribe(() => {
                this.updateAccessListForCurrentRoute();
            });
    }


    getMenuList(): MenuItem[] {
        let menuList: MenuItem[] = [];
        const storedMenu = this.tokenService.getLocalToken(env.TokenKeys.MenuList);

        if (storedMenu) {
            try {
                menuList = JSON.parse(storedMenu) as MenuItem[];
                menuList = this.filterMenuList(menuList);
            } catch (error) {
                menuList = [];
                console.error('Invalid menu list JSON:', error);
            }
        }

        return menuList;
    }

    filterMenuList(menuItems: MenuItem[]): MenuItem[] {
        if (!menuItems || menuItems.length === 0) {
            return [];
        }

        return menuItems
            .filter(menuItem => menuItem.status !== 2) // Remove items with status 2
            .map(menuItem => ({
                ...menuItem,
                children: menuItem.children ? this.filterMenuList(menuItem.children) : [],
            }));
    }

    getAccessListByName(name: string) {
        const menuList = this.getMenuList();
        const menuItem = this.findMenuByName(menuList, name);

        const webType = this.tokenService.getLocalToken(env.TokenKeys.WebType);
        let accessList = [];

        if (webType == WebTypeConstant.ADMIN) {
            if (menuItem?.authenticateAdminMenuAccessList) {
                accessList = menuItem.authenticateAdminMenuAccessList; // during authenticate api response AuthenticateAdminMenuAccessList
            } else if (menuItem?.adminMenuAccessList) {
                accessList = menuItem.adminMenuAccessList; // after sync api response AdminMenuAccessList
            }
            // accessList = menuItem ? menuItem.authenticateAdminMenuAccessList : [];
        } else if (webType == WebTypeConstant.MERCHANT) {
            accessList = menuItem ? menuItem.authenticateMerchantMenuAccessList : [];
        } else if (webType == WebTypeConstant.AGENTUNLIMITED) {
            accessList = menuItem ? menuItem.authenticateUnAgentMenuAccessList : [];
        } else if (webType == WebTypeConstant.ADMINSTAFF) {
            accessList = menuItem ? menuItem.authenticateAdminStaffMenuAccessList : [];
        } else if (webType == WebTypeConstant.MERCHANTSTAFF) {
            accessList = menuItem ? menuItem.authenticateMerchantStaffMenuAccessList : [];
        } else if (webType == WebTypeConstant.AGENTSTAFFUNLIMITED) {
            accessList = menuItem ? menuItem.authenticateUnAgentStaffMenuAccessList : [];
        }

        return accessList;
    }

    private findMenuByName(menuItems: any[], name: string): any {
        for (let item of menuItems) {
            if (item.name === name) {
                return item;
            }
            if (item.children && item.children.length > 0) {
                const found = this.findMenuByName(item.children, name);
                if (found) return found;
            }
        }
        return null;
    }

    getAccessListForCurrentRoute(): any[] {
        return this.accessListSubject.value;
    }

    updateAccessListForCurrentRoute(): void {
        const menuList = this.getMenuList();
        const urlTree = this.router.parseUrl(this.router.url);
        const currentRoute = '/' + urlTree.root.children['primary']?.segments.map(segment => segment.path).join('/') || '';

        const menuItem = this.findMenuByRoute(menuList, currentRoute);
        const webType = this.tokenService.getLocalToken(env.TokenKeys.WebType);
        let accessList = [];

        if (webType == WebTypeConstant.ADMIN) {
            if (menuItem?.authenticateAdminMenuAccessList) {
                accessList = menuItem.authenticateAdminMenuAccessList; // during authenticate api response AuthenticateAdminMenuAccessList
            } else if (menuItem?.adminMenuAccessList) {
                accessList = menuItem.adminMenuAccessList; // after sync api response AdminMenuAccessList
            }
            // accessList = menuItem ? menuItem.authenticateAdminMenuAccessList : [];
        } else if (webType == WebTypeConstant.MERCHANT) {
            accessList = menuItem ? menuItem.authenticateMerchantMenuAccessList : [];
        } else if (webType == WebTypeConstant.AGENTUNLIMITED) {
            accessList = menuItem ? menuItem.authenticateUnAgentMenuAccessList : [];
        } else if (webType == WebTypeConstant.ADMINSTAFF) {
            accessList = menuItem ? menuItem.authenticateAdminStaffMenuAccessList : [];
        } else if (webType == WebTypeConstant.MERCHANTSTAFF) {
            accessList = menuItem ? menuItem.authenticateMerchantStaffMenuAccessList : [];
        } else if (webType == WebTypeConstant.AGENTSTAFFUNLIMITED) {
            accessList = menuItem ? menuItem.authenticateUnAgentStaffMenuAccessList : [];
        }

        this.accessListSubject.next(accessList);
    }

    private findMenuByRoute(menuItems: any[], route: string): any {
        // Normalize the route by removing dynamic segments
        // const normalizedRoute = route.replace(/\/\d+$/, '').replace(/\/new$/, '');
        const segments = route.split('/').filter(Boolean);
        const lastSegment = segments[segments.length - 1];

        if (lastSegment === 'new' || this.isValidId(lastSegment)) {
            segments.pop(); // Remove last segment
        }

        const updatedRoute = '/' + segments.join('/');

        for (let item of menuItems) {
            if (item.route === updatedRoute) {
                return item;
            }
            if (item.children && item.children.length > 0) {
                const found = this.findMenuByRoute(item.children, route);
                if (found) return found;
            }
        }
        return null;
    }

    isValidId(segment: string): boolean {
        try {
            const decryptedId = this.base64Service.decode(segment);
            return !!decryptedId;
        } catch (error) {
            return false;
        }
    }
}
